// `namespace` is the modal's tag name, so the deal modal (assets/deal-quick-modal.js) and this
// one can both hold a product at once without duplicate ids.
function namespaceQuickModalProductHtml(html, namespace = 'quick-modal') {
  const productInfo = html.querySelector('product-info')
  if (!productInfo) return html

  const idMap = new Map()

  for (const element of productInfo.querySelectorAll('[id]')) {
    const currentId = element.getAttribute('id')
    if (!currentId) continue

    const namespacedId = `${currentId}--${namespace}`
    idMap.set(currentId, namespacedId)
    element.setAttribute('id', namespacedId)
  }

  const tokenReferenceAttributes = [
    'aria-controls',
    'aria-describedby',
    'aria-details',
    'aria-errormessage',
    'aria-labelledby',
    'aria-owns',
    'headers',
  ]
  const singleReferenceAttributes = [
    'data-controls',
    'data-modal',
    'data-modal-id',
    'data-open',
    'data-target',
    'for',
    'form',
    'list',
  ]
  const hashReferenceAttributes = ['href', 'xlink:href']

  for (const element of productInfo.querySelectorAll('*')) {
    for (const attributeName of tokenReferenceAttributes) {
      const value = element.getAttribute(attributeName)
      if (!value) continue

      const namespacedValue = value
        .split(/\s+/)
        .map((token) => idMap.get(token) || token)
        .join(' ')
      element.setAttribute(attributeName, namespacedValue)
    }

    for (const attributeName of singleReferenceAttributes) {
      const value = element.getAttribute(attributeName)
      if (!value) continue

      const reference = idMap.get(value)
      if (reference) element.setAttribute(attributeName, reference)
    }

    for (const attributeName of hashReferenceAttributes) {
      const value = element.getAttribute(attributeName)
      if (!value?.startsWith('#')) continue

      const hashReference = idMap.get(value.slice(1))
      if (hashReference) element.setAttribute(attributeName, `#${hashReference}`)
    }

    for (const attribute of Array.from(element.attributes)) {
      if (!attribute.value.includes('url(#')) continue

      const namespacedUrlReferences = attribute.value.replace(
        /url\(#([^)]+)\)/g,
        (match, id) => (idMap.has(id) ? `url(#${idMap.get(id)})` : match),
      )
      if (namespacedUrlReferences !== attribute.value) {
        element.setAttribute(attribute.name, namespacedUrlReferences)
      }
    }
  }

  const groupedRadios = productInfo.querySelectorAll(
    'product-colors input[type="radio"][name], product-pack-selector input[type="radio"][name], product-style input[type="radio"][name], variant-selects input[type="radio"][name]',
  )
  for (const radio of groupedRadios) {
    radio.name = `${radio.name}--${namespace}`
  }

  return html
}

window.namespaceQuickModalProductHtml = namespaceQuickModalProductHtml

// Pages loaded ahead by QuickModal#prefetch, by request URL. Text rather than the parsed document,
// since opening the modal moves nodes out of it. Short-lived so stock and prices stay fresh.
const QUICK_MODAL_PREFETCH_MS = 5 * 60 * 1000
const quickModalPrefetchCache = new Map()

function getQuickModalProductUrl(productHref, variantId = null) {
  const url = new URL(productHref, window.location.origin)
  url.searchParams.set('view', 'pdp-liquid-quick-add')
  if (variantId) url.searchParams.set('variant', variantId)
  return url.toString()
}

function getPrefetchedQuickModalText(url) {
  const cached = quickModalPrefetchCache.get(url)
  if (!cached) return null
  if (Date.now() - cached.time < QUICK_MODAL_PREFETCH_MS) return cached.text
  quickModalPrefetchCache.delete(url)
  return null
}

if (!customElements.get('quick-modal')) {
  class QuickModal extends HTMLElement {
    abortController = null
    cachedProductHtml = null
    cachedProductHref = null
    #escapeHandler = null
    #openRequestId = 0

    constructor() {
      super()
    }

    connectedCallback() {
      this.closeButton = this.querySelector('.js-quick-modal-close')
      this.content = this.querySelector('.js-quick-modal-content')
      this.init()
    }

    init() {
      this.addEventListener('click', this.close.bind(this))
      this.content.addEventListener('click', async (e) => {
        e.stopPropagation()
        // Hide accelerated checkout buttons if insider club upsell is checked
        if (this.querySelector('input[name="insider-club-upsell"]')?.checked) {
          this.querySelector('product-form shopify-accelerated-checkout')?.classList.add('hidden')
        } else {
          this.querySelector('product-form shopify-accelerated-checkout')?.classList.remove(
            'hidden',
          )
        }

        this.handleReviewStarsClick(e)
      })

      this.closeButton.addEventListener('click', this.close.bind(this))
      this.#escapeHandler = (e) => {
        if (e.key !== 'Escape') return
        // The reviews modal sits on top of the quick modal, so let it consume Escape first.
        if (document.querySelector('dynamic-modal')?.getIsOpen()) return
        // Same for a pdp-modal opened from the store credit block (its own handler closes it).
        if (document.querySelector('.js-quick-modal-store-credit-info .pdp-modal__overlay.is-open')) return
        // And the PDP price breakdown modal (assets/pdp-price-breakdown.js).
        if (document.querySelector('.pdp-price-breakdown-modal .pdp-modal__overlay.is-open')) return
        // Same for the custom-pack sheet and size chart opened inside the modal (each closes itself).
        if (this.hasOpenNestedModal()) return
        this.close()
      }
      document.addEventListener('keydown', this.#escapeHandler)
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this.#escapeHandler)
    }

    // True while the custom-pack sheet or a size chart is open on top of this modal.
    hasOpenNestedModal() {
      if (this.querySelector('t-custom-pack')?.isSheetOpen?.()) return true
      return Boolean(this.querySelector('size-chart-modal.is-open, size-chart-modal-tiles.is-open'))
    }

    // The content click listener stops propagation, so the document-level listener in
    // dynamic-modal.js never sees these clicks and the handler must be called directly.
    handleReviewStarsClick(e) {
      if (!e.target.closest('.js-product-star-rating')) return
      if (typeof reviewStarsClickHandler !== 'function') return
      reviewStarsClickHandler(e)
    }

    async open(isRebuyRecommendation = false, variantId = null) {
      const openRequestId = ++this.#openRequestId
      const isLiveStreamingOpen = Boolean(this.dataset.liveStream)
      if (isLiveStreamingOpen) {
        console.log('[Live streaming quick add] Opening modal', {
          openRequestId,
          productHref: this.productHref,
          variantId,
          modalActive: this.classList.contains('active'),
          modalLoading: this.classList.contains('loading'),
        })
      }
      // a11y: release inert, remember the trigger, move focus into the dialog
      this.opener = document.activeElement
      this.removeAttribute('inert')
      this.classList.add('active')
      this.classList.add('loading')
      this.closeButton?.focus()
      document.body.style.setProperty('overflow', 'hidden', 'important')
      this.cachedProductHref = this.productHref
      this.dataset.isRebuyRecommendation = isRebuyRecommendation
      const didFetchProduct = await this.fetchProduct(variantId, openRequestId)
      if (!didFetchProduct || !this.#isCurrentOpenRequest(openRequestId)) return
      if (isLiveStreamingOpen) {
        console.log('[Live streaming quick add] Product fetch finished', {
          openRequestId,
          productHref: this.productHref,
          cachedProductHref: this.cachedProductHref,
          hasCachedProductHtml: Boolean(this.cachedProductHtml),
          modalActive: this.classList.contains('active'),
        })
      }
      await this.updateQuickAddModal()
      if (!this.#isCurrentOpenRequest(openRequestId)) return
      if (isLiveStreamingOpen) {
        console.log('[Live streaming quick add] Modal content updated', {
          openRequestId,
          productHref: this.productHref,
          productId: this.productInfo?.dataset.productId,
          modalActive: this.classList.contains('active'),
          modalLoading: this.classList.contains('loading'),
        })
      }

      const productInfo = this.querySelector('product-info')
      if (productInfo.dataset.productId && RecentlyViewed) {
        RecentlyViewed.addProduct(
          productInfo.dataset.productId,
          productInfo.dataset.productCategory5,
        )
      }
      dataLayerPageView()
    }

    close() {
      if (this.dataset.liveStream) {
        console.log('[Live streaming quick add] Closing modal', {
          productHref: this.productHref,
          modalLoading: this.classList.contains('loading'),
          abortedRequest: Boolean(this.abortController),
        })
      }
      // Release the custom-pack sheet (and its scroll lock) before this modal drops its own.
      this.querySelector('t-custom-pack')?.closeSheet?.()
      this.classList.remove('active')
      // One-shot opener flag; <product-info> keeps its copy until the next open, so the
      // closing fade doesn't change layout.
      delete this.dataset.customPackOnly
      this.setAttribute('inert', '')
      if (this.opener && document.contains(this.opener)) this.opener.focus()
      this.opener = null
      document.body.style.removeProperty('overflow')
    }

    async fetchProduct(variantId = null, openRequestId) {
      if (!this.productHref) return false
      const isLiveStreamingOpen = Boolean(this.dataset.liveStream)
      this.abortController?.abort()
      const abortController = new AbortController()
      this.abortController = abortController
      const viewFullDetailsUrl = new URL(this.productHref, window.location.origin)
      if (this.dataset.liveStream) {
        viewFullDetailsUrl.searchParams.set('live_stream', this.dataset.liveStream)
      }
      this.viewFullDetails.href = `${viewFullDetailsUrl.pathname}${viewFullDetailsUrl.search}`

      const fullUrl = getQuickModalProductUrl(this.productHref, variantId)

      try {
        if (isLiveStreamingOpen) {
          console.log('[Live streaming quick add] Fetching product', {
            openRequestId,
            fullUrl,
            variantId,
          })
        }
        const response = await this.fetchProductText(fullUrl, abortController.signal)
        const responseText = response.text
        const html = namespaceQuickModalProductHtml(
          new DOMParser().parseFromString(responseText, 'text/html'),
          this.localName,
        )
        if (!this.#isCurrentOpenRequest(openRequestId)) return false
        this.cachedProductHtml = html
        if (isLiveStreamingOpen) {
          console.log('[Live streaming quick add] Product fetch succeeded', {
            openRequestId,
            fullUrl,
            responseStatus: response.status,
            responseLength: responseText.length,
            hasProductInfo: Boolean(html.querySelector('product-info')),
          })
        }
        return true
      } catch (error) {
        if (isLiveStreamingOpen) {
          console.error('[Live streaming quick add] Product fetch failed', {
            openRequestId,
            fullUrl,
            error,
            aborted: error.name === 'AbortError',
          })
        }
        if (error.name === 'AbortError') {
          return false
        } else {
          if (this.#isCurrentOpenRequest(openRequestId)) this.close()
          throw error
        }
      } finally {
        if (this.abortController === abortController) this.abortController = null
      }
    }

    #isCurrentOpenRequest(openRequestId) {
      return openRequestId === this.#openRequestId
    }

    // Uses a prefetched page when there is one; a failed prefetch falls back to the network.
    async fetchProductText(url, signal) {
      const prefetchedText = await getPrefetchedQuickModalText(url)?.catch(() => null)
      if (prefetchedText) return { status: 200, text: prefetchedText }

      const response = await fetch(url, { signal })
      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`)
      }
      return { status: response.status, text: await response.text() }
    }

    // Loads a product the shopper is about to open (hover, tap or focus on its opener), so the
    // open skips the skeleton. Only prefetched products are cached; other opens fetch as before.
    // Returns the page's text promise, so callers can load several one after another.
    prefetch(productHref, variantId = null) {
      const url = getQuickModalProductUrl(productHref, variantId)
      const cachedText = getPrefetchedQuickModalText(url)
      if (cachedText) return cachedText

      const text = fetch(url).then(function readPrefetchedText(response) {
        if (!response.ok) throw new Error(`Failed to prefetch product page: HTTP error ${response.status}`)
        return response.text()
      })
      text.catch(function dropFailedPrefetch() {
        if (quickModalPrefetchCache.get(url)?.text === text) quickModalPrefetchCache.delete(url)
      })
      quickModalPrefetchCache.set(url, { text, time: Date.now() })
      return text
    }

    clearQuickAddModal() {
      this.productInfo?.querySelector('pdp-purchase-test')?.restoreControl()
      this.productInfo?.querySelector('pdp-purchase-test')?.remove()
      this.productInfo?.removeAttribute('data-purchase-test-active')
      this.resetCustomPackState()
      this.gallery.innerHTML = ''
      this.header.innerHTML = ''
      this.variantSelector.innerHTML = ''
      this.discountBarWrapper.innerHTML = ''
      this.productForm.innerHTML = ''
      this.colorSelector.innerHTML = ''
      this.customPackWrapper.innerHTML = ''
      this.packSizeSelector.innerHTML = ''
      this.insiderWrapper.innerHTML = ''
      this.storeCreditInfoWrapper.innerHTML = ''
      this.uiFooter.innerHTML = ''
      this.uiFooter.style.removeProperty('display')
      this.usoWrapper.innerHTML = ''
      this.jsonDataWrapper.innerHTML = ''
      this.productDescription.innerHTML = ''
      this.sizeChartModalWrapper.innerHTML = ''
      this.expectedShippingMessage.innerHTML = ''
    }

    // <product-info> is a singleton reused across opens: drop the previous product's custom-pack
    // picks and bundle flags so the next product starts outside bundle mode with empty slots.
    // (The dataset copy in updateQuickAddModal re-applies the new product's bundle-* values.)
    resetCustomPackState() {
      const productInfo = this.productInfo
      if (!productInfo) return
      productInfo.bundleSelections = []
      productInfo.dataset.bundleMode = 'false'
      delete productInfo.dataset.bundleProductHandle
    }

    // The store credit block's info modals need component-pdp-modal.css and its balance cell needs
    // pdp-store-credit-info.js. The product page loads both through the block's snippet, but only
    // the block itself is copied here, so load whichever the current page doesn't have yet.
    ensureStoreCreditAssets() {
      const assets = this.cachedProductHtml.querySelectorAll(
        'link[href*="component-pdp-modal.css"], script[src*="pdp-store-credit-info.js"]',
      )

      assets.forEach((asset) => {
        const isStylesheet = asset.tagName === 'LINK'
        const url = isStylesheet ? asset.getAttribute('href') : asset.getAttribute('src')
        const fileName = url.split('?')[0].split('/').pop()
        const attribute = isStylesheet ? 'href' : 'src'
        if (document.querySelector(`${asset.tagName.toLowerCase()}[${attribute}*="${fileName}"]`)) {
          return
        }

        const element = document.createElement(isStylesheet ? 'link' : 'script')
        if (isStylesheet) {
          element.rel = 'stylesheet'
          element.href = url
        } else {
          element.src = url
          element.defer = true
        }
        document.head.appendChild(element)
      })
    }

    async updateQuickAddModal() {
      this.clearQuickAddModal()

      // Custom-pack builder (hidden until bundle mode). Moved first so the lookups below
      // (gallery, variant-selects, product-colors) only ever see the PDP's own copies,
      // not the builder sheet's.
      const customPackWrapper = this.cachedProductHtml.querySelector('.t-custom-pack-wrapper')
      if (customPackWrapper) this.customPackWrapper.appendChild(customPackWrapper)

      const productGallery = this.cachedProductHtml.querySelector('product-gallery')
      if (productGallery) {
        productGallery.dataset.disableZoom = true
        this.gallery.appendChild(productGallery)
        // Force play videos on Safari
        this.gallery.querySelectorAll('video').forEach((video) => {
          video.play()
        })
      }

      const productHeader = this.cachedProductHtml.querySelector('.liquid-pdp__header')
      if (productHeader) {
        this.header.appendChild(productHeader)
        const productTitle = productHeader.querySelector('.js-product-title')
        const sourceProductInfo = this.cachedProductHtml.querySelector('product-info')
        if (this.dataset.useProductTitle === 'true' && productTitle && sourceProductInfo?.dataset.productTitle)
          productTitle.textContent = sourceProductInfo.dataset.productTitle
      }

      const productVariantSelector = this.cachedProductHtml.querySelector('variant-selects')
      if (productVariantSelector) {
        this.variantSelector.appendChild(productVariantSelector)
      }

      const productForm = this.cachedProductHtml.querySelector(
        '.liquid-pdp__builder-button-wrapper',
      )
      if (productForm) {
        this.productForm.appendChild(productForm)
        const form = this.productForm.querySelector('form.js-product-form')
        if (form && this.dataset.liveStream) {
          const liveStreamProperty = document.createElement('input')
          liveStreamProperty.type = 'hidden'
          liveStreamProperty.name = 'properties[_live_stream]'
          liveStreamProperty.value = this.dataset.liveStream
          form.appendChild(liveStreamProperty)
        }
      }

      // Include the inert experiment component in the modal's purchase area.
      // Its CSS flag decides whether it runs; the existing form owns cart adds.
      const purchaseTest = this.cachedProductHtml.querySelector('pdp-purchase-test')
      if (purchaseTest) this.productForm.before(purchaseTest)

      const promoCountdownCallout = this.cachedProductHtml.querySelector(
        '.promo-countdown-callout--pdp',
      )
      if (promoCountdownCallout) {
        this.productForm.appendChild(promoCountdownCallout)
      }

      const productColorSelector = this.cachedProductHtml.querySelector('product-colors')
      const isWeeklyDeal = this.dataset.weeklyDeal === 'true'

      function updateProductColorSelectorForWeeklyDeals(colorSelector) {
        if (!colorSelector) return colorSelector

        if (!isWeeklyDeal) return colorSelector

        const wrapper = colorSelector.querySelector('.liquid-pdp__product-color-swatches-wrapper')
        if (!wrapper) return colorSelector

        wrapper.querySelectorAll('label').forEach((label) => {
          const input = label.querySelector('input[data-is-weekly-deal]')
          if (!input || input.dataset.isWeeklyDeal !== 'true') {
            label.remove()
          } else {
            label.classList.remove('liquid-pdp__product-color-swatch--hidden')
          }
        })

        const clearanceWrapper = colorSelector.querySelector('.js-clearance-swatches')

        let hasClearanceSwatches = false
        if (clearanceWrapper) {
          clearanceWrapper.querySelectorAll('label').forEach((label) => {
            const input = label.querySelector('input[data-is-weekly-deal]')
            if (!input || input.dataset.isWeeklyDeal !== 'true') {
              label.remove()
            } else {
              label.classList.remove('liquid-pdp__product-color-swatch--hidden')
              hasClearanceSwatches = true
            }
          })
        }

        const toggleButton = wrapper.querySelector('.liquid-pdp__singles-swatches__toggle')
        const clearanceSwatchesTitle = colorSelector.querySelector('.js-clearance-swatches-title')
        if (toggleButton) toggleButton.remove()
        if (clearanceSwatchesTitle) clearanceSwatchesTitle.remove()
        if (clearanceWrapper && !hasClearanceSwatches) clearanceWrapper.remove()

        return colorSelector
      }

      const updatedProductColorSelector =
        updateProductColorSelectorForWeeklyDeals(productColorSelector)

      // Customize buttons and the "Custom Pack" tile stay: the builder is wired up here too.
      if (updatedProductColorSelector) this.colorSelector.appendChild(updatedProductColorSelector)

      const productPackSizeSelector = this.cachedProductHtml.querySelector('product-pack-selector')
      this.packSizeSelector.style.display = 'block'
      if (productPackSizeSelector) {
        this.packSizeSelector.appendChild(productPackSizeSelector)
        if (productPackSizeSelector.style.display === 'none') {
          this.packSizeSelector.style.display = 'none'
        }
      } else {
        this.packSizeSelector.style.display = 'none'
      }

      const productInsiderWrapper = this.cachedProductHtml.querySelector('.js-insider-club-upsell')
      if (productInsiderWrapper) {
        this.insiderWrapper.appendChild(productInsiderWrapper)
      }

      const storeCreditInfo = this.cachedProductHtml.querySelector('.pdp-store-credit-info')
      this.storeCreditInfoWrapper.style.display = 'block'
      if (storeCreditInfo) {
        this.ensureStoreCreditAssets()
        this.storeCreditInfoWrapper.appendChild(storeCreditInfo)
      } else {
        this.storeCreditInfoWrapper.style.display = 'none'
      }

      if (this.afterpayWidget?.connectedCallback) {
        this.afterpayWidget.connectedCallback()
      }

      const productUiFooter = this.cachedProductHtml.querySelector('.js-liquid-pdp__ui-footer')
      if (productUiFooter) {
        this.uiFooter.appendChild(productUiFooter)
        // Eligibility is controlled by product-info's final-sale attribute.
        // Reading computed styles here uses the previous product's dataset.
      }

      const usoWrapper = this.cachedProductHtml.querySelector('.js-uso-wrapper')
      if (usoWrapper) {
        this.usoWrapper.appendChild(usoWrapper)
      } else {
        this.usoWrapper.style.display = 'none'
      }

      const productJsonData = this.cachedProductHtml.querySelector('pdp-product-context')
      if (productJsonData) {
        this.jsonDataWrapper.appendChild(productJsonData)
      }

      // TODO: Remove this once we have a proper product description for quick add modal
      // const productDescription = this.cachedProductHtml.querySelector('.liquid-pdp__details')
      const productDescription = null
      this.productDescription.style.display = 'block'
      if (productDescription) {
        this.productDescription.appendChild(productDescription)
        if (productDescription.style.display === 'none') {
          this.productDescription.style.display = 'none'
        }
      } else {
        this.productDescription.style.display = 'none'
      }

      const sizeChartModal = this.cachedProductHtml.querySelector(
        'size-chart-modal, size-chart-modal-tiles',
      )
      if (sizeChartModal) {
        this.sizeChartModalWrapper.appendChild(sizeChartModal)
      }

      const expectedShippingMessage = this.cachedProductHtml.querySelector(
        '.cart-summary__expected-shipping',
      )
      if (expectedShippingMessage) {
        this.expectedShippingMessage.appendChild(expectedShippingMessage)
      } else {
        this.expectedShippingMessage.style.display = 'none'
      }

      // Copy dataset from source product info to quick add product info
      const sourceDataset = this.cachedProductHtml.querySelector('product-info').dataset
      Object.entries(sourceDataset).forEach(([key, value]) => {
        this.productInfo.dataset[key] = value
      })
      // Override product info dataset for quick add modal
      this.productInfo.dataset.cartSource = 'Quick View'
      this.productInfo.dataset.pdpType = 'popup'
      this.productInfo.dataset.pageType = this.dataset.pageType
      if (this.dataset.liveStream) {
        this.productInfo.dataset.liveStream = this.dataset.liveStream
      } else {
        delete this.productInfo.dataset.liveStream
      }
      // productInfo is reused by the singleton modal; never carry a clearance-only
      // variant filter into a regular product view.
      if (this.dataset.showOnlyClearance) {
        this.productInfo.dataset.showOnlyClearance = this.dataset.showOnlyClearance
      } else {
        delete this.productInfo.dataset.showOnlyClearance
      }
      // Openers that always add a single unit hide the quantity control
      // (quick-add-modal.css). Cleared otherwise — the modal is a singleton.
      if (this.dataset.hideQuantitySelector) {
        this.productInfo.dataset.hideQuantitySelector = this.dataset.hideQuantitySelector
      } else {
        delete this.productInfo.dataset.hideQuantitySelector
      }
      // Custom-pack-only opener (single-row gallery on the sale page): quick-add-modal.css hides
      // the pack and product selectors for it. Cleared otherwise, the modal being a singleton.
      if (this.dataset.customPackOnly) {
        this.productInfo.dataset.customPackOnly = this.dataset.customPackOnly
      } else {
        delete this.productInfo.dataset.customPackOnly
      }
      if (this.dataset.simplifiedQuickView) {
        this.productInfo.dataset.simplifiedQuickView = this.dataset.simplifiedQuickView
      }
      this.productInfo.dataset.updateUrl = 'false'
      this.productInfo.dataset.isRebuyRecommendation = this.dataset.isRebuyRecommendation
      this.productInfo.dataset.ruleId = this.dataset.ruleId

      if (this.productInfo.dataset.isWeeklyDeal === 'true') {
        this.packSizeSelector.classList.add('visually-hidden')
      } else {
        this.packSizeSelector.classList.remove('visually-hidden')
      }

      // Product price — move the whole .pdp-price-and-discount block (not just
      // the inner .pdp-product-price-wrapper) so the grid container, per-item
      // price, manual discount code, and the in-modal price-update traversals
      // (liquid-pdp.js relies on .closest('.pdp-price-and-discount__top')) all
      // carry over and the spacing/layout matches the native PDP.
      const priceAndDiscount = this.cachedProductHtml.querySelector('.pdp-price-and-discount')
      const productPriceWrapper = priceAndDiscount?.querySelector('.pdp-product-price-wrapper')
      if (priceAndDiscount) {
        this.discountBarWrapper.appendChild(priceAndDiscount)
        if (!productPriceWrapper?.classList.contains('hidden')) {
          this.discountBarWrapper.classList.remove('visually-hidden')
        } else {
          this.discountBarWrapper.classList.add('visually-hidden')
        }
      } else {
        this.discountBarWrapper.classList.add('visually-hidden')
      }

      // Non-US: Fontana pack lookup + ATC hold for the injected product
      this.productInfo.handleQuickModalContentUpdate?.()

      this.classList.remove('loading')
      // Lets sections that post-process the injected PDP markup (e.g. clearance
      // tier pricing) run once the modal content is in place.
      document.dispatchEvent(
        new CustomEvent('quick-modal-content-updated', { detail: { modal: this } }),
      )
    }

    get productHref() {
      return this.dataset.productHref
    }

    get gallery() {
      return this.querySelector('.js-quick-modal-gallery')
    }

    get header() {
      return this.querySelector('.js-quick-modal-header')
    }

    get discountBarWrapper() {
      return this.querySelector('.js-quick-modal-discount-bar-wrapper')
    }

    get variantSelector() {
      return this.querySelector('.js-quick-modal-variant-selector')
    }

    get productForm() {
      return this.querySelector('.js-product-form')
    }

    get colorSelector() {
      return this.querySelector('.js-quick-modal-color-selector')
    }

    get customPackWrapper() {
      return this.querySelector('.js-quick-modal-custom-pack')
    }

    get packSizeSelector() {
      return this.querySelector('.js-quick-modal-pack-size-selector')
    }

    get viewFullDetails() {
      return this.querySelector('.js-quick-modal-view-full-details')
    }

    get productInfo() {
      return this.querySelector('product-info')
    }

    get insiderWrapper() {
      return this.querySelector('.js-quick-modal-insider-wrapper')
    }

    get storeCreditInfoWrapper() {
      return this.querySelector('.js-quick-modal-store-credit-info')
    }

    get uiFooter() {
      return this.querySelector('.js-quick-modal-ui-footer')
    }

    get afterpayWidget() {
      return this.querySelector('afterpay-widget')
    }

    get usoWrapper() {
      return this.querySelector('.js-quick-modal-uso-wrapper')
    }

    get jsonDataWrapper() {
      return this.querySelector('.js-quick-modal-json-data')
    }

    get productDescription() {
      return this.querySelector('.js-quick-modal-product-description')
    }

    get sizeChartModalWrapper() {
      return this.querySelector('.size-chart-modal-wrapper')
    }

    get expectedShippingMessage() {
      return this.querySelector('.js-quick-modal-expected-shipping-message')
    }
  }
  // Exposed so the sale page's deal modal (assets/deal-quick-modal.js) can extend it.
  window.QuickModal = QuickModal
  customElements.define('quick-modal', QuickModal)
}
